<template>
  <AppLayout>
    <div class="tender-detail-page">
      <template v-if="tender">
        <div class="tender-detail">
        <!-- Header -->
        <div class="header" :style="headerStyle">
          <div class="container header-content">
            <img src="/logo.png" alt="Logo" class="header-logo" />
            <div class="tender-info">
              <h2 class="tender-title">{{ tender.title }}</h2>
                <div v-if="tender._supplier" class="supplier-note" style="margin-top:8px;color:#1f2937;">
                  Submitting as: <strong>{{ tender._supplier.name }}</strong>
                </div>
                <div class="tender-meta">
                  <div class="deadline">
                    <div class="deadline-container">
                      <div class="countdown-big">{{ remaining.days }}</div>
                      <div class="countdown-labels">
                        <div class="countdown-hms">{{ pad(remaining.hours) }}:{{ pad(remaining.minutes) }}:{{ pad(remaining.seconds) }}</div>
                        <div class="countdown-muted">days remaining</div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
        <!-- Content -->
        <div class="content">
          <div class="container content-grid">
            <!-- Main -->
            <div class="main-content">
              <section id="desc-section" class="content-section">
                <h3 class="text-primary">Tender Description</h3>
                <div class="content-text">{{ tender.description }}</div>
              </section>
              <section v-if="tender.objectives" id="objectives-section" class="content-section">
                <h3 class="text-primary">Objectives</h3>
                <div class="content-text">{{ tender.objectives }}</div>
              </section>
              <section v-if="tender.scope" id="scope-section" class="content-section">
                <h5>Scope of Work</h5>
                <div class="content-text">{{ tender.scope }}</div>
              </section>
              <section v-if="tender.requirements" id="requirements-section" class="content-section">
                <h2>Requirements</h2>
                <div class="content-text">{{ tender.requirements }}</div>
              </section>
              <section v-if="tender.eligibilityCriteria" id="eligibility-section" class="content-section">
                <h3>Eligibility Criteria</h3>
                <div class="content-text">{{ tender.eligibilityCriteria }}</div>
              </section>
              <section v-if="tender.evaluationCriteria" id="evaluation-section" class="content-section">
                <h3>Evaluation Criteria</h3>
                <div class="content-text">{{ tender.evaluationCriteria }}</div>
              </section>
              <section v-if="tender.submissionGuidelines" id="submission-section" class="content-section">
                <h3>Submission Guidelines</h3>
                <div class="content-text">{{ tender.submissionGuidelines }}</div>
              </section>
              <section v-if="tender.contractTerms" id="contract-section" class="content-section">
                <h3>Contract Terms</h3>
                <div class="content-text">{{ tender.contractTerms }}</div>
              </section>
              <section v-if="tender.paymentTerms" id="payment-section" class="content-section">
                <h3>Payment Terms</h3>
                <div class="content-text">{{ tender.paymentTerms }}</div>
              </section>
              <!-- Application -->
              <section id="application-section" class="content-section application-section">
                <h3>Submit Your Application</h3>
                <form class="application-form" @submit.prevent="submitApplication">
                  <!-- Company Info -->
                  <div class="form-section">
                    <h4 class="mt-3">Company Information</h4>
                      <div style="display:flex; gap:11px; align-items:center; margin-bottom:8px;">
                        <div v-if="tender._supplier" style="display:flex; align-items:center; gap:10px;">
                          <img v-if="tender._supplier?.picture || tender._supplier?.logo" :src="tender._supplier.picture || tender._supplier.logo" alt="Supplier Logo" style="width:56px; height:56px; object-fit:cover; border-radius:6px; border:1px solid #e6eef6" />
                          <div style="font-size:13px">Submitting as: <strong>{{ tender._supplier?.name }}</strong></div>
                        </div>
                        <div style="margin-left:auto; text-align:right; font-size:11px; color:#374151">
                          <!--<div v-if="supplierEmail">Email: <a :href="`mailto:${supplierEmail}`">{{ supplierEmail }}</a></div>-->
                          <div v-if="supplierPhone">Tel: <a :href="`tel:${supplierPhone}`">{{ supplierPhone }}</a></div>
                        </div>
                      </div>
                      <div class="form-grid">
                      <input v-model="application.companyName" :readonly="!!tender._supplier" placeholder="Company Name" required />
                      <input v-model="application.contactPerson" placeholder="Contact Person" required />
                      <input v-model="application.companyEmail" :readonly="!!tender._supplier" placeholder="Company Email" required />
                      <input v-model="application.companyPhone" placeholder="Company Phone" required />
                      <input v-model="application.companyWebsite" placeholder="Company Website" />
                      <input v-model="application.companyAddress" placeholder="Company Address" required />
                      <input v-model="application.natureOfBusiness" placeholder="Nature of Business" required />
                      <input v-model="application.companyRegistration" placeholder="Registration / VAT Number" required />
                    </div>
                  </div>
                  <!-- Proposal -->
                  <div class="form-section">
                    <h4 class="mt-1">Proposal</h4>
                    <textarea v-model="application.proposalSummary" rows="4" placeholder="Summary" />
                    <textarea v-model="application.technicalProposal" rows="4" placeholder="Technical Proposal" />
                    <textarea v-model="application.financialProposal" rows="4" placeholder="Financial Proposal" />
                  </div>
                  <div class="form-group" style="margin-top:11px;">
                    <label>Upload Quotation / Documents (optional)</label>
                    <input type="file" ref="filesInput" multiple @change="onFilesChange" />
                  </div>
                  <div class="form-actions">
                    <button class="btn btn-primary btn-large" :disabled="submitting">
                      {{ submitting ? 'Submitting...' : 'Submit Application' }}
                    </button>
                  </div>
                </form>
              </section>
            </div>
            <!-- Sidebar -->
            <aside class="sidebar">
              <div class="sidebar-nav sidebar-card">
                <h3>Navigate</h3>
                <ul>
                  <li><a @click.prevent="scrollToSection('desc-section')">Description</a></li>
                  <li v-if="tender.objectives"><a @click.prevent="scrollToSection('objectives-section')">Objectives</a></li>
                  <li v-if="tender.scope"><a @click.prevent="scrollToSection('scope-section')">Scope</a></li>
                  <li><a @click.prevent="scrollToSection('application-section')">Apply</a></li>
                  <li v-if="quotationItems && quotationItems.length"><a @click.prevent="scrollToSection('quotation-section')">Quotation</a></li>
                  <li v-if="tender.requiredSupplierDocuments && tender.requiredSupplierDocuments.length"><a @click.prevent="scrollToSection('required-docs-section')">Required Documents</a></li>
                </ul>
              </div>
              <div class="sidebar-card">
                <div v-if="tender._supplier" class="supplier-card">
                  <div class="supplier-header">
                    <img v-if="tender._supplier?.picture || tender._supplier?.logo" :src="tender._supplier.picture || tender._supplier.logo" alt="Supplier Logo" class="supplier-logo" />
                    <div class="supplier-meta">
                      <div class="supplier-name">{{ tender._supplier?.name || tender.companyName || 'Supplier' }}</div>
                      <div class="supplier-contact">
                        <div v-if="supplierEmail"><a :href="`mailto:${supplierEmail}`">{{ supplierEmail }}</a></div>
                        <div v-if="supplierPhone"><a :href="`tel:${supplierPhone}`">{{ supplierPhone }}</a></div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Quotation section: list tender items and allow entering unit price -->
                <div id="quotation-section" class="form-section" v-if="quotationItems.length">
                  <h4 class="mt-1">Your Quotation</h4>
                  <table style="width:100%; border-collapse:collapse; margin-top:8px">
                    <thead>
                      <tr style="text-align:left; border-bottom:1px solid #e6eef6;">
                        <th style="padding:8px">Item</th>
                        <th style="padding:8px">Unit</th>
                        <th style="padding:8px">Qty</th>
                        <th style="padding:8px">Unit Price ({{ quoteCurrency }})</th>
                        <th style="padding:8px">Line Total ({{ quoteCurrency }})</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="it in quotationItems" :key="it.id" style="border-bottom:1px solid #f1f5f9">
                        <td style="padding:8px">{{ it.name }}</td>
                        <td style="padding:8px">{{ it.unit || '-' }}</td>
                        <td style="padding:8px">{{ it.quantity }}</td>
                        <td style="padding:8px">
                          <input type="number" min="0" step="0.01" v-model.number="it.unitPrice" class="form-control" style="width:140px" />
                        </td>
                        <td style="padding:8px">{{ formatMoney((it.unitPrice || 0) * (it.quantity || 1)) }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colspan="4" style="text-align:right; padding:8px; font-weight:600">Total</td>
                        <td style="padding:8px; font-weight:700">{{ formatMoney(quoteTotal()) }}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p><strong>Deadline:</strong> {{ formatDate(tender.submissionDeadline) }}</p>
                <p><strong>Category:</strong> {{ tender.category }}</p>
              </div>
              <div id="required-docs-section" class="sidebar-card" v-if="tender.requiredSupplierDocuments && tender.requiredSupplierDocuments.length">
                <h3>Required Documents</h3>
                <div v-for="(req, idx) in tender.requiredSupplierDocuments" :key="req.id" style="margin-bottom:8px; display:flex; align-items:center; gap:8px;">
                  <div style="flex:1; min-width:0">
                    <div style="font-weight:600; color:#0b1220">{{ req.supplierDocumentType?.name || 'Document' }}</div>
                    <div style="font-size:11px; color:#64748b">Required document</div>
                  </div>
                  <div style="flex:1; display:flex; align-items:center; justify-content:flex-end; gap:8px; min-width:0">
                    <div style="max-width:220px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#374151; font-size:13px; text-align:right">
                      {{ requiredDocsUploads[idx]?.file?.name || 'No file selected' }}
                    </div>
                    <input type="file" @change="(e) => onRequiredFileChange(e, idx)" />
                  </div>
                </div>
                <div class="muted" style="font-size:11px; margin-top:8px">Please attach these documents when submitting your application. They will be reviewed before supplier prequalification.</div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="loading">
      <div class="loading-page">Loading tender…</div>
    </template>
    <template v-else>
      <div class="error-page">
        <p>Tender not found</p>
        <RouterLink v-if="isAdminRoute" to="/admin/tenders" class="btn btn-outline-primary btn-sm mt-2">Back to Tenders</RouterLink>
      </div>
    </template>
  </div>
  </AppLayout>
</template>



<script>
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import AppLayout from '@/components/AppLayout.vue'
import Swal from 'sweetalert2'

export default {
  components: { AppLayout },
  props: ['slug', 'id'],
  setup(props) {
    const route = useRoute()
    const isAdminRoute = computed(() => (route.path || '').startsWith('/admin/tenders/'))
    const tender = ref(null)
    const loading = ref(true)
    const submitting = ref(false)
    const requiredDocsUploads = ref([])
    const quotationItems = ref([])
    const application = ref({
      companyName: '',
      contactPerson: '',
      companyEmail: '',
      companyPhone: '',
      companyWebsite: '',
      companyAddress: '',
      natureOfBusiness: '',
      companyRegistration: '',
      proposalSummary: '',
      technicalProposal: '',
      financialProposal: '',
    })
    const files = ref([])

    function onRequiredFileChange(e, idx) {
      const f = (e.target.files && e.target.files[0]) || null
      if (!requiredDocsUploads.value[idx]) return
      requiredDocsUploads.value[idx].file = f
    }

    function formatDate(d) {
      return new Date(d).toLocaleDateString()
    }

    function getDaysRemaining() {
      if (!tender.value || !tender.value.submissionDeadline) return 0
      return Math.max(0, Math.ceil((new Date(tender.value.submissionDeadline) - Date.now()) / 86400000))
    }

    // Live countdown state (days/hours/minutes/seconds)
    const remaining = ref({ days: 0, hours: 0, minutes: 0, seconds: 0 })
    let countdownTimer = null

    function pad(n) { return String(n || 0).padStart(2, '0') }

    function updateCountdown() {
      if (!tender.value || !tender.value.submissionDeadline) {
        remaining.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        return
      }
      const end = new Date(tender.value.submissionDeadline).getTime()
      let diff = Math.max(0, end - Date.now())
      if (diff <= 0) {
        remaining.value = { days: 0, hours: 0, minutes: 0, seconds: 0 }
        if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null }
        return
      }
      const secs = Math.floor(diff / 1000)
      const days = Math.floor(secs / 86400)
      const hours = Math.floor((secs % 86400) / 3600)
      const minutes = Math.floor((secs % 3600) / 60)
      const seconds = secs % 60
      remaining.value = { days, hours, minutes, seconds }
    }

    function scrollToSection(id) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    }

    function scrollToApplication() {
      scrollToSection('application-section')
    }

    async function fetchTender() {
      loading.value = true
      try {
        const API = process.env.VUE_APP_URL || window.location.origin || 'http://localhost:3000'
        const params = new URLSearchParams(window.location.search)
        const supplierToken = params.get('s')
        const routeId = props.id || route.params?.id || window.location.pathname.split('/').pop()

        // Admin route: fetch by ID using authenticated endpoint
        if (isAdminRoute.value && routeId) {
          let rawToken = null
          try { rawToken = JSON.parse(localStorage.getItem('access_token')) } catch (e) { rawToken = localStorage.getItem('access_token') }
          const authToken = (rawToken && typeof rawToken === 'object') ? (rawToken.access_token || rawToken.token || rawToken) : rawToken
          const res = await fetch(`${API}/tender/${routeId}`, {
            headers: { Accept: 'application/json', ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) }
          })
          if (!res.ok) throw new Error('Not found')
          const body = await res.json()
          tender.value = body.data || body
        } else {
          // Public route: fetch by slug
          const routeSlug = props.slug || routeId
          const url = supplierToken ? `${API}/tender/public/${routeSlug}?s=${encodeURIComponent(supplierToken)}` : `${API}/tender/public/${routeSlug}`
          const res = await fetch(url)
          if (!res.ok) throw new Error('Not found')
          const body = await res.json()
          const payload = body.data || body
          tender.value = payload.tender || payload
          if (payload.supplier) {
            tender.value._supplier = payload.supplier
            application.value.companyName = payload.supplier.name || application.value.companyName
            application.value.companyEmail = payload.supplier.email || application.value.companyEmail
          }
        }

        // initialize required document upload slots
        // store the supplierDocumentType id (if available) so we can include it in the documentTypes payload
        requiredDocsUploads.value = (tender.value?.requiredSupplierDocuments || []).map((r) => ({
          id: r.supplierDocumentType?.id || r.id,
          name: r.supplierDocumentType?.name || 'Document',
          file: null,
        }))
        // If this tender is linked to a purchase request, prefer using its items for quotations.
        // Only attempt to fetch the linked purchase request when an auth token exists; public views
        // should fall back to the tender's own items.
        const linkedPrId = tender.value?.purchaseRequestId
        console.log('[TenderDetail] linked purchase request id:', linkedPrId)
        try {
          let rawToken = null
          try { rawToken = JSON.parse(localStorage.getItem('access_token')) } catch (e) { rawToken = localStorage.getItem('access_token') }
          const authToken = (rawToken && typeof rawToken === 'object') ? (rawToken.access_token || rawToken.token || rawToken) : rawToken

          if (linkedPrId) {
            const headers = { 'Accept': 'application/json', 'Authorization': `Bearer ${authToken}` }
            const prRes = await fetch(`${API}/purchase-requests/${linkedPrId}`, { headers })
            const prBody = await prRes.json()
            console.log('[TenderDetail] linked purchase request response', prBody)
            const prItems = prBody.data.items
            console.log('Linked purchase request items', prBody.data.items)
            console.log("Quotation ITEMS", prItems)


            if (prRes.ok) {
              console.log('Response is ')
              //const prBody = await prRes.json()
             
              quotationItems.value = (Array.isArray(prItems) ? prItems : []).map((it, idx) => {
                const prod = it.product || it.Product || {}
                const name = prod.name || it.description || it.title || (`Item ${idx+1}`)
                const quantity = it.quantityProcessed ?? it.processedQuantity ?? it.quantity ?? it.requestedQuantity ?? it.quantityRequested ?? it.requestedQty ?? 1
                const unit = prod.unit?.name || it.unit || it.unitName || ''
                return { id: it.id || prod.id || `pr-${idx}`, name, quantity, unit, unitPrice: 0 }
              })
            } else {
              console.debug('[TenderDetail] linked purchase request fetch failed, falling back to tender items')
            }
          } 
        } catch (e) {
          console.debug('[TenderDetail] failed to fetch linked purchase request items', e)
        }

        // initialize quotation items from tender items (if any) when not already set by linked PR
        if (!quotationItems.value || quotationItems.value.length === 0) {
          const rawItems = tender.value?.items || tender.value?.tenderItems || tender.value?.TenderItem || tender.value?.products || tender.value?.productsList || tender.value?.tender_products || tender.value?.tender_items || []
          quotationItems.value = (Array.isArray(rawItems) ? rawItems : []).map((it, idx) => {
            const prod = it.product || it.Product || {}
            const name = it.name || prod.name || it.description || it.title || it.itemName || (`Item ${idx+1}`)
            const quantity = it.quantityProcessed ?? it.processedQuantity ?? it.quantity ?? it.qty ?? it.requestedQuantity ?? it.quantityRequested ?? it.tenderedQuantity ?? it.requestedQty ?? 1
            const unit = it.unit || it.unitName || prod.unit || (prod.unit && prod.unit.name) || ''
            return { id: it.id || prod.id || `i-${idx}`, name, quantity, unit, unitPrice: 0 }
          })
        }
      } catch (e) {
        tender.value = null
      } finally {
        loading.value = false
      }
    }

    function quoteTotal(){
      return quotationItems.value.reduce((s, it) => s + (Number(it.unitPrice || 0) * Number(it.quantity || 1)), 0)
    }

    const headerStyle = computed(() => {
      const img = tender.value?.banner || tender.value?.bannerUrl || tender.value?.headerImage || tender.value?.coverImage || tender.value?.image
      if (!img) {
        // subtle purple with a very light orange tint at the edge
        return { background: 'linear-gradient(90deg, rgba(43,40,108,1) 0%, rgba(43,40,108,1) 70%, rgba(245,158,11,0.06) 100%)' }
      }
      return {
        // minimal orange radial + linear overlay over the banner (very low alpha)
        backgroundImage: `radial-gradient(circle at 30% 30%, rgba(245,158,11,0.06), rgba(245,158,11,0.02) 20%), linear-gradient(rgba(43,40,108,0.25), rgba(245,158,11,0.08)), url(${img})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    })

    function onFilesChange(e) {
      files.value = Array.from(e.target.files || [])
    }

    const supplierEmail = computed(() => {
      return tender.value?._supplier?.email || tender.value?.contactEmail || tender.value?.email || ''
    })

    const supplierPhone = computed(() => {
      return tender.value?._supplier?.phone || tender.value?.contactPhone || tender.value?.phone || ''
    })

    const quoteCurrency = computed(() => {
      // prefer explicit currency codes stored on tender; fall back to common fields
      return tender.value?.currency || tender.value?.currencyCode || 'USD'
    })

    async function submitApplication() {
      if (!application.value.companyName || !application.value.companyEmail || !application.value.contactPerson || !application.value.companyPhone) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Company name, contact person, email and phone are required' })
        return
      }

      // If tender specifies required supplier documents, ensure user attached them
      const requiredDocs = tender.value?.requiredSupplierDocuments || []
      if (requiredDocs.length > 0) {
        const missing = requiredDocsUploads.value.filter((r) => !r.file)
        if (missing.length > 0) {
          const names = missing.map((r) => r.name).join(', ')
          Swal.fire({ icon: 'warning', title: 'Missing documents', text: `Please attach required documents: ${names}` })
          return
        }
      }
      // Ensure company address, nature of business and registration are provided
      if (!application.value.companyAddress || !String(application.value.companyAddress).trim()) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Company address is required' })
        return
      }
      if (!application.value.natureOfBusiness || !String(application.value.natureOfBusiness).trim()) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Nature of business is required' })
        return
      }
      if (!application.value.companyRegistration || !String(application.value.companyRegistration).trim()) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Company registration / VAT number is required' })
        return
      }

      // Ensure required proposal fields are present
      if (!application.value.technicalProposal || !String(application.value.technicalProposal).trim()) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Technical proposal is required' })
        return
      }
      if (!application.value.financialProposal || !String(application.value.financialProposal).trim()) {
        Swal.fire({ icon: 'warning', title: 'Validation', text: 'Financial proposal is required' })
        return
      }

      // Quotation price validation (when items exist)
      if (quotationItems.value.length > 0) {
        const unfilled = quotationItems.value.filter(it => {
          const v = it.unitPrice
          return v === '' || v === null || v === undefined || (typeof v === 'number' && isNaN(v))
        })
        if (unfilled.length > 0) {
          Swal.fire({ icon: 'warning', title: 'Prices required', text: 'Please fill in the unit price for all quotation items.' })
          return
        }
        const negative = quotationItems.value.filter(it => Number(it.unitPrice) < 0)
        if (negative.length > 0) {
          Swal.fire({ icon: 'warning', title: 'Invalid prices', text: 'Unit prices cannot be negative.' })
          return
        }
        const zeroItems = quotationItems.value.filter(it => Number(it.unitPrice) === 0)
        if (zeroItems.length > 0) {
          const names = zeroItems.map(it => it.name || 'Item').join(', ')
          const { isConfirmed } = await Swal.fire({
            icon: 'question',
            title: 'Confirm zero prices',
            text: `You have entered 0 for: ${names}. Do you want to submit anyway?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, submit',
            cancelButtonText: 'Cancel',
          })
          if (!isConfirmed) return
        }
      }

      submitting.value = true
      try {
        const routeSlug = props.slug || window.location.pathname.split('/').pop()
        const API = process.env.VUE_APP_URL || window.location.origin || 'http://localhost:3000'
        const formData = new FormData()
        formData.append('companyName', application.value.companyName)
        // backend expects these keys: tenderId, contactPerson, email, phone
        formData.append('tenderId', tender.value?.id || '')
        formData.append('contactPerson', application.value.contactPerson)
        formData.append('email', application.value.companyEmail)
        formData.append('phone', application.value.companyPhone)
        formData.append('companyWebsite', application.value.companyWebsite)
        formData.append('companyAddress', application.value.companyAddress)
        // map natureOfBusiness into companyExperience on the submission model
        formData.append('companyExperience', application.value.natureOfBusiness)
        formData.append('companyRegistration', application.value.companyRegistration)
        formData.append('proposalSummary', application.value.proposalSummary)
        formData.append('technicalProposal', application.value.technicalProposal)
        formData.append('financialProposal', application.value.financialProposal)
        // append required document files first
        for (const slot of requiredDocsUploads.value) {
          if (slot.file) formData.append('documents', slot.file)
        }
        // append any optional files
        for (let i = 0; i < files.value.length; i++) {
          formData.append('documents', files.value[i])
        }

        // Build `documentTypes` payload describing uploaded files and their optional type ids
        try {
          const docTypesArr = []
          // required docs were appended first so map those by filename -> type id
          for (const slot of requiredDocsUploads.value) {
            if (slot.file) docTypesArr.push({ originalname: slot.file.name, id: slot.id || null })
          }
          // include optional files as entries with null type id (server will ignore or match by name/index)
          for (let i = 0; i < files.value.length; i++) {
            docTypesArr.push({ originalname: files.value[i].name, id: null })
          }
          if (docTypesArr.length > 0) formData.append('documentTypes', JSON.stringify(docTypesArr))
        } catch (e) {
          console.debug('[TenderDetail] could not build documentTypes payload', e)
        }

        const params = new URLSearchParams(window.location.search)
        const supplierToken = params.get('s')
        const submitUrl = supplierToken ? `${API}/tender/public/${routeSlug}/submit?s=${encodeURIComponent(supplierToken)}` : `${API}/tender/public/${routeSlug}/submit`
        console.debug('[TenderDetail] submitting to', submitUrl)
        // optional: log form keys (do not log file contents)
        try {
          const keys = []
          for (const pair of formData.entries()) {
            keys.push(pair[0])
          }
          console.debug('[TenderDetail] formData keys:', keys.join(', '))
        } catch (e) {
          console.debug('[TenderDetail] could not enumerate formData')
        }

          // include quotation payload as JSON
          let quotationPayload = null
          try{
            const q = { items: quotationItems.value.map(it => ({ id: it.id, name: it.name, quantity: it.quantity, unit: it.unit, unitPrice: it.unitPrice })), total: quoteTotal() }
            quotationPayload = JSON.stringify(q)
            formData.append('quotation', quotationPayload)
            // also append as a Blob (helps some multer configs and preserves content-type)
            try { formData.append('quotation_json', new Blob([quotationPayload], { type: 'application/json' })) } catch (err) { console.debug('[TenderDetail] could not append quotation blob', err) }
          }catch(e){ console.debug('[TenderDetail] could not append quotation', e) }

        const res = await fetch(submitUrl, {
          method: 'POST',
          body: formData,
          redirect: 'follow',
          mode: 'cors',
        })

        console.log('Submission response', res)

        if (res.ok) {
          // Success page may be HTML redirect result — treat as success
          Swal.fire({ icon: 'success', title: 'Application submitted', text: 'Application submitted successfully' })
          submitting.value = false
          return
        }

        // Try to read JSON first, fall back to text (HTML error page)
        let errBody = null
        try {
          errBody = await res.json()
        } catch (e) {
          try {
            const txt = await res.text()
            // Attempt to extract a useful message from HTML result page
            const m = txt.match(/<p>(.*?)<\/p>/i)
            errBody = { message: m ? (m[1] || 'Submission failed') : 'Submission failed' }
          } catch (e2) {
            errBody = { message: 'Submission failed' }
          }
        }
        const message = errBody?.message || errBody?.error || 'Submission failed'
        // If multer returned "Unexpected field", try submitting via a native form POST as a fallback
        if (String(message).toLowerCase().includes('unexpected field')) {
          try {
            // build and submit a hidden form so multer receives the multipart exactly like a browser form
            const form = document.createElement('form')
            form.method = 'POST'
            form.enctype = 'multipart/form-data'
            form.action = submitUrl
            form.style.display = 'none'

            const appendInput = (name, value) => {
              const input = document.createElement('input')
              input.type = 'hidden'
              input.name = name
              input.value = value == null ? '' : String(value)
              form.appendChild(input)
            }

            appendInput('companyName', application.value.companyName)
            appendInput('tenderId', tender.value?.id || '')
            appendInput('contactPerson', application.value.contactPerson)
            appendInput('email', application.value.companyEmail)
            appendInput('phone', application.value.companyPhone)
            appendInput('companyWebsite', application.value.companyWebsite)
            appendInput('companyAddress', application.value.companyAddress)
            appendInput('companyExperience', application.value.natureOfBusiness)
            appendInput('companyRegistration', application.value.companyRegistration)
            appendInput('proposalSummary', application.value.proposalSummary)
            appendInput('technicalProposal', application.value.technicalProposal)
            appendInput('financialProposal', application.value.financialProposal)
            appendInput('comments', application.value.comments || '')

            // attach files using DataTransfer onto file inputs
            const addFilesInput = (filesArr) => {
              if (!filesArr || filesArr.length === 0) return
              const fileInput = document.createElement('input')
              fileInput.type = 'file'
              fileInput.name = 'documents'
              fileInput.multiple = true
              try {
                const dt = new DataTransfer()
                for (const f of filesArr) dt.items.add(f)
                fileInput.files = dt.files
              } catch (e) {
                // Some browsers may not allow programmatic assignment of FileList
              }
              form.appendChild(fileInput)
            }

            // include optional files
            addFilesInput(files.value)
            // include quotation as a hidden input in fallback form so it is not lost
            if (quotationPayload) appendInput('quotation', quotationPayload)
            // include required docs uploads if any
            const reqFiles = requiredDocsUploads.value.map(r => r.file).filter(Boolean)
            addFilesInput(reqFiles)

            document.body.appendChild(form)
            form.submit()
            return
          } catch (fallbackErr) {
              console.error('[TenderDetail] fallback form submit failed', fallbackErr)
            }
        }

        throw new Error(message)
      } catch (err) {
        console.error('Tender submit error', err)
        const text = (err && (err.message || err.error)) || 'Submission failed'
        Swal.fire({ icon: 'error', title: 'Submission failed', text })
      } finally {
        submitting.value = false
      }
    }

    onMounted(() => {
      // fetch tender and start countdown updater
      fetchTender().then(() => {
        updateCountdown()
        if (!countdownTimer) countdownTimer = setInterval(updateCountdown, 1000)
      })
    })

    onUnmounted(() => {
      if (countdownTimer) clearInterval(countdownTimer)
    })

    function formatMoney(v){
      const n = Number(v || 0)
      try{
        const currency = tender.value?.currency || tender.value?.currencyCode || 'USD'
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, maximumFractionDigits:2 }).format(n)
      }catch(e){ return n.toFixed(2) }
    }

    return {
      tender,
      loading,
      submitting,
      isAdminRoute,
      application,
      files,
      formatDate,
      getDaysRemaining,
      remaining,
      pad,
      scrollToSection,
      scrollToApplication,
      submitApplication,
      onFilesChange,
      requiredDocsUploads,
      onRequiredFileChange,
      supplierEmail,
      supplierPhone,
      quoteCurrency,
      headerStyle,
      quotationItems,
      quoteTotal,
      formatMoney,
    }
  }
}
</script>

<style scoped>
/* Modern, professional tender detail styles */
.tender-detail { font-family:  "Times New Roman", Times, serif; color: #0f172a; }
h3,h2{
  font-family: "Segoe UI", "Segoe UI Variable", "Segoe UI Symbol",
               "Segoe UI Emoji", Arial, sans-serif;
  opacity: 0.9;              
}

.tender-detail-page{
  background: #FFF5EB;
  min-height: 100vh;
}

input{
  border-right: none !important;
  border-left: none !important; 
  border-top: none !important;
  outline: none !important;
}

h3{
  color: #171725 !important;
  font-size: 1.5rem !important;
  opacity: 0.7 !important;
}
.header { background: linear-gradient(90deg,#2b286c 0%, #3b2f86 50%, #f59e0b 100%); border-bottom: 1px solid rgba(230,238,246,0.6); padding: 28px 0; }
.header-content { display:grid; grid-template-columns: auto 1fr; align-items:center; gap:20px; }
.tender-info { justify-self:center; text-align:center; max-width: 78%; }
.header .tender-info h2 { color: #fff; }
.tender-ref { color:#475569; font-size:11px; margin-bottom:6px; }
.tender-info h1 { font-size:22px; margin:0 0 6px; font-weight:700; color:#0b1220; }
.supplier-note { background: #f1f8ff; padding:6px 10px; border-radius:6px; display:inline-block; margin-top:6px; color:#0b1220 }
.tender-meta { margin-top:10px; display:flex; gap:11px; align-items:center; justify-content:center; color:#475569; font-size:13px }
.tender-meta .category { background:#eef2ff; padding:6px 10px; border-radius:6px; color:#3730a3; font-weight:600 }
.tender-meta .deadline { display:flex; gap:11px; align-items:center }

/* Countdown styles */
.deadline-container { display:flex; align-items:center; gap:11px }
.countdown-big { font-size:48px; font-weight:800; color:#ffd6a3; line-height:1; background: rgba(0,0,0,0.08); padding:8px 13px; border-radius:8px; min-width:72px; text-align:center }
.countdown-labels { display:flex; flex-direction:column; justify-content:center }
.countdown-hms { font-weight:700; color:#fff; opacity:0.95; font-size:13px }
.countdown-muted { font-size:11px; color:rgba(255,255,255,0.85); opacity:0.9 }
.btn-large { background:#0f172a; color:#fff; border:none; padding:10px 13px; border-radius:8px; cursor:pointer }
.btn-large:hover{ opacity:0.95 }

.content { padding:10px 0; }
.content-grid { display:grid; grid-template-columns: 1fr 700px; gap:28px; align-items:start }
.content-section { background:#fff; border:1px solid #eef2f6; padding:18px; margin-bottom:13px }
.content-section h2 { margin:0 0 10px; font-size:13px; color:#0b1220 }
.content-text { color:#334155; line-height:1.6 }

.application-section .application-form { display:flex; flex-direction:column; gap:11px }
.form-section h3 { margin:0 0 8px; font-size:13px; color:#0b1220 }
.form-grid { display:grid; grid-template-columns: repeat(2,1fr); gap:10px }
.form-grid input, textarea { width:100%; padding:10px; border:1px solid #e6eef6; font-size:13px }
.form-actions { display:flex; justify-content:flex-end }
.form-actions .btn { border-radius:8px; }

.sidebar { position:relative }
.sidebar-card { background:#fff; border:1px solid #eef2f6; padding:11px;  margin-bottom:13px }
.sidebar-card h3{ margin:0 0 8px; font-size:15px }
.sidebar-card ul { list-style:none; padding:0; margin:0 }
.sidebar-card ul li { margin:8px 0 }
.sidebar-card a { color:#0f172a; text-decoration:none }

/* Navigate card specific styling */
.sidebar-nav {
  background: linear-gradient(180deg, #ffffff, #fbfbff);
  border: 1px solid #e6eef6;
  padding: 11px;
  border-radius: 8px;
  box-shadow: 0 4px 11px rgba(15, 23, 42, 0.04);
}
.sidebar-nav h3 { font-size:16px; margin-bottom:10px; color:#0b1220 }
.sidebar-nav ul { padding: 6px 0 }
.sidebar-nav ul li { margin:6px 0 }
.sidebar-nav ul li a {
  display:block;
  padding:8px 10px;
  border-radius:6px;
  color:#0f172a;
  text-decoration:none;
  transition: background .15s ease, color .15s ease, transform .08s ease;
}
.sidebar-nav ul li a:hover {
  background: #f1f5f9;
  color: #071133;
  transform: translateX(4px);
}
.sidebar-nav ul li a:active { transform: translateX(2px) }

.loading-page, .error-page { padding:48px; text-align:center; color:#475569 }

@media (max-width: 900px) {
  .content-grid { grid-template-columns: 1fr; }
  .header-content { grid-template-columns: 1fr; }
  .tender-info { justify-self:center; max-width:100% }
}

/* Supplier card */
.supplier-header { display:flex; gap:11px; align-items:center }
.supplier-logo { width:56px; height:56px; object-fit:cover; border-radius:8px; border:1px solid #e6eef6 }
.supplier-meta { display:flex; flex-direction:column }
.supplier-name { font-weight:700; color:#0b1220 }
.supplier-contact a { color:#3730a3; text-decoration:none }

.header-logo { width:160px; height:auto; border-radius:3px; padding:10px; background: linear-gradient(180deg, rgba(187, 185, 185, 0.95), rgba(255,255,255,0.75)); box-shadow: 0 10px 30px rgba(11,17,32,0.18); margin-right:18px; object-fit:contain; border: 1px solid rgba(15,23,42,0.06) }
.tender-title { color:#ffffff !important; font-size:32px !important; max-width:1000px; word-break:break-all; text-transform:capitalize }

</style>
